import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable, forkJoin } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { JsonMapperService } from 'src/app/core/services/mapper/mapper.service';
import { Echantillon } from '../../models/echantillon.model';
import { ProduitsService } from 'src/app/data/habilitation/services/produits/produits.service';
import { OperateursService } from 'src/app/data/intervenant/services/operateurs/operateurs.service';
import { ClearCache } from 'src/app/core/services/cache/cache.service';
import { ReferentielService } from 'src/app/data/declaration/services/referentiel/referentiel.service';
import { OrganismesService } from 'src/app/data/intervenant/services/organismes/organismes.service';
import { EchantillonFilterExport } from '../../models/echantillon-filter.model';
import { Tournee } from '../../../tournee/models/tournee.model';
import { Commission } from '../../models/commission.model';
import { Cahier } from '../../../habilitation/models/cahier.model';
import { EchantillonExport } from '../../models/echantillon-export.model';

@Injectable({
    providedIn: 'root'
})
export class EchantillonsService {
    constructor(
        private readonly http: HttpClient,
        private readonly mapper: JsonMapperService,
        private readonly refService: ReferentielService,
        private readonly produitsService: ProduitsService,
        private readonly operateursService: OperateursService,
        private readonly organismesService: OrganismesService
    ) {}

    @ClearCache
    clearCache() {}

    getEchantillonsByCdcs(idOrganisme: number, idsCdcs: number[], aPreleve: boolean): Observable<Echantillon[]> {
        return forkJoin([
            this.http.get<object[]>(`/api/declaration/private/organismes/${idOrganisme}/echantillons?cdc=${idsCdcs.join(',')}&aPreleve=${aPreleve}`),
            this.refService.getReferentiel(),
            this.organismesService.getOrganismes()
        ]).pipe(
            map(([echantillons, ref, organismes]) => {
                return this.mapper.deserializeArray(echantillons, Echantillon, Object.assign({ organismes }, ref));
            }),
            this.produitsService.getProduitPipe(),
            this.operateursService.getOperateurPipe<Echantillon>(),
            tap(echantillons => {
                echantillons.forEach(item => {
                    this.operateursService.getInformationsDomaine(item.idOperateur).subscribe(infos => (item.infosOperateur = infos));
                });
            })
        );
    }

    /**
     * Récupère la liste des données pour alimenter les filtres de la page Export des échantillons
     * @param idOrganisme - L'id de l'organisme connecté
     */
    getExportFilterEchantillons(idOrganisme: number): Observable<EchantillonFilterExport[]> {
        return this.http
            .get<EchantillonFilterExport[]>(`/api/declaration/private/organismes/${idOrganisme}/echantillons/filter-export`)
            .pipe(map(echantillonsFilterExport => this.mapper.deserializeArray(echantillonsFilterExport, EchantillonFilterExport)));
    }

    /**
     * Export des échantillons
     * @param idOrganisme - Id organisme
     * @param anneeControle - Année de contrôle
     * @param millesime - Millésime
     * @param cahiers - Cahiers
     * @param tournee - Tournée
     * @param commission - Commission
     */
    exportEchantillons(
        idOrganisme: number,
        anneeControle: number,
        millesime: number,
        cahiers: Cahier[] | null,
        tournee: Tournee | null,
        commission: Commission | null
    ): Observable<EchantillonExport[]> {
        let params = new HttpParams();
        params = anneeControle ? params.set('annee', anneeControle.toString()) : params;
        params = millesime ? params.set('millesime', millesime.toString()) : params;
        params = !cahiers || cahiers.length === 0 ? params : params.set('cahiers', cahiers.map(cahier => cahier.id).join(','));
        params = !tournee ? params : params.set('idTournee', tournee.id.toString());
        params = !commission ? params : params.set('idCommission', commission.id.toString());
        return this.http
            .get<EchantillonExport[]>(`/api/declaration/private/organismes/${idOrganisme}/echantillons/export`, { params })
            .pipe(map(echantillons => this.mapper.deserializeArray(echantillons, EchantillonExport)));
    }
}
